import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import SatelliteDataReportHeader from '../../../../components/SatelliteDataReport/SatelliteDataReportHeader';
import { useTranslation } from 'react-i18next';
import SatelliteDataReportSubHeader from '../../../../components/SatelliteDataReport/SatelliteDataReportSubHeader';
import { getEmojiFromColor } from '../../../../globals/utils';
import { getAMSatelliteReportDetails } from '../../../../api/amSatelliteReport';

const AmSatelliteReportSummaryPage = ({ farmId, language, accessKey }) => {
  const { t } = useTranslation();
  const [farmData, setFarmData] = useState(null);

  const fetchReportDetails = async () => {
    try {
      if (accessKey) {
        const response = await getAMSatelliteReportDetails(
          farmId,
          language,
          accessKey
        );
        setFarmData(response.data);
      } else {
        console.error('Access key is required to fetch report details.');
      }
    } catch (err) {
      console.error('Error fetching farm satellite data report details:', err);
    }
  };

  useEffect(() => {
    if (farmId && language) {
      fetchReportDetails();
    }
  }, [farmId, language]);

  const basicMetrics = [
    { label: t('Farmer Name'), value: farmData?.farm_details.farmer_name },
    { label: t('Farm Name'), value: farmData?.farm_details.farm_name },
    { label: t('Assessment Date'), value: farmData?.farm_details.date },
    { label: t('Area (acres)'), value: farmData?.farm_details.farm_area },
    { label: t('Soil Texture'), value: farmData?.farm_details.soil_texture },
  ];

  const parameters = [
    {
      name: t('Overall Crop Growth'),
      text:
        farmData?.satellite_data_parameters.overall_crop_growth?.text || 'N/A',
      color:
        farmData?.satellite_data_parameters.overall_crop_growth?.color || 'red',
    },
    {
      name: t('Soil Moisture'),
      // text: farmData?.satellite_data_parameters.soil_moisture?.text || 'N/A',
      text: t('Moderate'),
      // color: farmData?.satellite_data_parameters.soil_moisture?.color || 'red',
      color: 'orange',
    },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#ffffff',
        '@media print': {
          pageBreakInside: 'avoid',
          margin: '0',
          padding: '0',
        },
      }}
    >
      <SatelliteDataReportHeader />

      {/* Satellite Report Basic Details */}
      <Grid
        id="satellite-report-basic-details"
        container
        spacing={1}
        sx={{
          mb: 2,
          p: 2,
          mt: 1,
          '@media print': {
            pageBreakInside: 'avoid',
            marginBottom: '5mm',
            padding: '2mm',
          },
        }}
      >
        {basicMetrics.map((metric, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
              }}
            >
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={6}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px !important',
                    '@media print': {
                      padding: '1.5mm !important',
                    },
                  }}
                >
                  <Typography sx={{ color: '#237D41', fontSize: '14px' }}>
                    <strong>{metric.label}:</strong>
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={6}
                  my="auto"
                  sx={{
                    bgcolor: '#237D411A',
                    padding: '8px !important',
                    '@media print': {
                      padding: '1.5mm !important',
                      backgroundColor: '#237D411A !important',
                    },
                  }}
                >
                  <Typography
                    sx={{ color: '#237D41', my: 'auto', fontSize: '14px' }}
                  >
                    {metric.value}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Satellite Report Metrics */}
      <Paper
        sx={{
          mb: 1,
          borderRadius: 0,
          boxShadow: 0,
          '@media print': {
            pageBreakInside: 'avoid',
            marginBottom: '3mm',
            boxShadow: 'none',
          },
        }}
        id="satellite-report-metrics"
      >
        <SatelliteDataReportSubHeader text={t('Parameter')} />
        <Box
          sx={{
            px: 2,
            py: 2,
            bgcolor: '#237D410D',
            borderRadius: 0,
            '@media print': {
              padding: '2mm',
              backgroundColor: '#237D410D !important',
            },
          }}
        >
          {parameters.map((param, index) => {
            const { name, color, text } = param;
            const emoji = getEmojiFromColor(color);

            return (
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid
                      item
                      xs={6}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '4px !important',
                        '@media print': {
                          padding: '1mm !important',
                        },
                      }}
                    >
                      <Typography sx={{ color: '#237D41', fontSize: '14px' }}>
                        {name}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      my="auto"
                      sx={{
                        bgcolor: '#FFFFFF',
                        padding: '4px 0 4px 12px !important',
                        display: 'flex',
                        flexDirection: 'row',
                        border: '0.2px solid #D9D9D9',
                        '@media print': {
                          padding: '1mm 0 1mm 2mm !important',
                          backgroundColor: '#FFFFFF !important',
                          border: '0.1mm solid #D9D9D9',
                        },
                      }}
                    >
                      <Typography
                        sx={{ color: color, my: 'auto', fontSize: '14px' }}
                      >
                        {emoji}
                      </Typography>
                      <Typography
                        sx={{
                          color: color,
                          my: 'auto',
                          paddingLeft: '8px',
                          fontSize: '14px',
                          '@media print': {
                            paddingLeft: '1.5mm',
                          },
                        }}
                      >
                        {text}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

export default AmSatelliteReportSummaryPage;
